import { Delete, Save } from '@mui/icons-material';
import { Button, Grid, TextField } from '@mui/material';
import { DatePicker, StaticDatePicker } from '@mui/x-date-pickers';
import { Vacation as VacationType } from '@thro-types/leihbar';

import { add, endOfDay, format, isWithinInterval, startOfDay } from 'date-fns';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import {
  useBookingsQuery,
  useConflictingVacationBookingsQuery,
  useVacationsMutation,
  useVacationsQuery
} from 'api/hooks/leihbar';
import { ConfirmationDialog, CreateButton } from 'app/components';
import { AlertWithTitle } from 'app/components/AlertWithTitle';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import { AvailableColorsVacation } from './components/AvailableColorsVacation';
import { VacationPickersDay } from './components/VacationPickersDay';

interface VacationProps {
  lenderGroupId: number;
}

export const Vacation = ({ lenderGroupId }: VacationProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [selectedVacation, setSelectedVacation] = useState<VacationType | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { control, handleSubmit, setValue, formState, watch } = useForm<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    defaultValues: { startDate: null, endDate: null }
  });

  const { data: bookingsData } = useConflictingVacationBookingsQuery(
    (!!watch('startDate') && !!watch('endDate')) || selectedVacation !== null,
    {
      params: {
        lenderGroupId,
        startDate: selectedVacation
          ? selectedVacation.startDate
          : watch('startDate')?.toISOString(),
        endDate: selectedVacation ? selectedVacation.endDate : watch('endDate')?.toISOString()
      }
    }
  );
  const { data } = useVacationsQuery(lenderGroupId);
  const { creation, update, deletion } = useVacationsMutation();

  const { data: bookings } = useBookingsQuery({
    params: {
      withUserInfo: true,
      lenderGroupId: lenderGroupId
    }
  });

  const handleSubmitFunc = (data: { startDate: Date | null; endDate: Date | null }) => {
    if (isCreateMode) {
      handleCreate(data);
    } else if (selectedVacation) {
      handleUpdate();
    }
  };

  const handleCreate = async ({ startDate, endDate }) => {
    try {
      if (!formState.isValid) {
        return;
      }
      await creation.mutateAsync({
        startDate,
        endDate,
        lenderGroupId
      });
      dispatch(
        AlertActions.success(
          t(translations.pages.leihbarOpeningHoursSettings.vacation.alerts.successfulCreate())
        )
      );
    } catch (error) {
      dispatch(
        AlertActions.error(
          t(translations.pages.leihbarOpeningHoursSettings.vacation.alerts.failedCreate())
        )
      );
    } finally {
      setIsCreateMode(false);
      setSelectedVacation(null);
    }
  };

  const handleUpdate = async () => {
    try {
      if (!selectedVacation) return;
      await update.mutateAsync({
        id: selectedVacation.id,
        data: {
          startDate: selectedVacation.startDate,
          endDate: selectedVacation.endDate,
          lenderGroupId
        }
      });
      dispatch(
        AlertActions.success(
          t(translations.pages.leihbarOpeningHoursSettings.vacation.alerts.successfulUpdate())
        )
      );
    } catch (error) {
      dispatch(
        AlertActions.error(
          t(translations.pages.leihbarOpeningHoursSettings.vacation.alerts.failedUpdate())
        )
      );
    } finally {
      setSelectedVacation(null);
    }
  };

  const handleDelete = async () => {
    try {
      if (!selectedVacation) return;
      await deletion.mutateAsync(selectedVacation.id);
      dispatch(
        AlertActions.success(
          t(translations.pages.leihbarOpeningHoursSettings.vacation.alerts.successfulDelete())
        )
      );
    } catch (error) {
      dispatch(
        AlertActions.error(
          t(translations.pages.leihbarOpeningHoursSettings.vacation.alerts.failedDelete())
        )
      );
    } finally {
      setSelectedVacation(null);
      setDeleteDialogOpen(false);
    }
  };

  const handleDateSelect = (day: Date | null) => {
    setIsCreateMode(false);
    if (!day) {
      return;
    }
    const vacation = data.vacations.find(vacation => {
      return isWithinInterval(day, {
        start: startOfDay(new Date(vacation.startDate)),
        end: endOfDay(new Date(vacation.endDate))
      });
    });
    if (vacation) {
      setSelectedVacation(vacation);
      setValue('endDate', null);
    } else {
      setSelectedVacation(null);
      setIsCreateMode(true);

      setValue('startDate', startOfDay(day));
      //set endDate 1 Day later as startDate
      setValue('endDate', endOfDay(add(day, { days: 1 })));
    }
  };

  const renderAlertBookings = () => {
    if (!bookingsData?.bookings?.length) {
      return <></>;
    }
    const preparationTime = bookingsData.bookings[0].article?.product?.preparationTime;

    const getUnitText = () => {
      if (!preparationTime?.value) {
        return t(translations.pages.leihbar.product.noPreparationTime());
      }
      return `${preparationTime?.value} ${t(
        translations.pages.leihbar.product.create.units[preparationTime.unit]()
      )}`;
    };

    return (
      <Grid item>
        <AlertWithTitle
          alertTitle={t(
            translations.pages.leihbarOpeningHoursSettings.vacation.conflictingBookings()
          )}
        >
          <Grid container flexDirection={'column'} gap={1}>
            {bookingsData.bookings.map(booking => (
              <Link
                key={booking.id}
                to={`/leihbar/product/${booking.article?.product?.id}?tab=bookings`}
              >
                {`${booking.article?.product?.name}: ${format(
                  new Date(booking.startTime),
                  'dd.MM.yyyy hh:mm'
                )} - ${format(new Date(booking.endTime), 'dd.MM.yyyy hh:mm')} - 
                  ${t(
                    translations.pages.leihbar.product.create.preparationTime()
                  )}: ${getUnitText()} - 
                  ${booking.order?.name}`}
              </Link>
            ))}
          </Grid>
        </AlertWithTitle>
      </Grid>
    );
  };

  const renderButtons = () => {
    if (isCreateMode) {
      return (
        <Grid container justifyContent="end">
          <CreateButton type="submit" disabled={!formState.isValid} />
        </Grid>
      );
    } else if (selectedVacation) {
      return (
        <Grid container justifyContent="end" gap={2}>
          <Button
            variant="contained"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
            startIcon={<Delete />}
          >
            {t(translations.common.delete())}
          </Button>
          <Button
            variant="contained"
            type="submit"
            startIcon={<Save />}
            disabled={!selectedVacation.startDate || !selectedVacation.endDate}
          >
            {t(translations.common.save())}
          </Button>
        </Grid>
      );
    }
  };

  const renderDatePickers = () => {
    return (
      <form onSubmit={handleSubmit(handleSubmitFunc)}>
        <Grid container gap={2}>
          {isCreateMode && (
            <>
              <Grid container gap={2} justifyContent={'space-between'}>
                <Controller
                  control={control}
                  name="startDate"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <DatePicker
                      value={field.value}
                      minDate={new Date()}
                      inputFormat="dd.MM.yyyy"
                      onChange={field.onChange}
                      renderInput={params => <TextField {...params} size="small" />}
                      label={t(translations.pages.leihbarOpeningHoursSettings.vacation.startDate())}
                    />
                  )}
                />
                -
                <Controller
                  control={control}
                  name="endDate"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <DatePicker
                      value={field.value}
                      minDate={watch('startDate') || new Date()}
                      inputFormat="dd.MM.yyyy"
                      onChange={field.onChange}
                      renderInput={params => <TextField {...params} size="small" />}
                      label={t(translations.pages.leihbarOpeningHoursSettings.vacation.endDate())}
                      defaultCalendarMonth={watch('startDate') || new Date()}
                    />
                  )}
                />
              </Grid>
              {renderAlertBookings()}
            </>
          )}
          {selectedVacation && (
            <Grid container gap={2} justifyContent={'space-between'}>
              <DatePicker
                minDate={new Date()}
                inputFormat="dd.MM.yyyy"
                value={selectedVacation.startDate}
                onChange={newValue => {
                  if (!selectedVacation || !newValue) return;
                  //@ts-ignore
                  setSelectedVacation(vacation => ({
                    ...vacation,
                    startDate: startOfDay(new Date(newValue))
                  }));
                }}
                renderInput={params => <TextField {...params} size="small" />}
                label={t(translations.pages.leihbarOpeningHoursSettings.vacation.startDate())}
              />
              -
              <DatePicker
                minDate={selectedVacation.startDate || new Date()}
                inputFormat="dd.MM.yyyy"
                value={selectedVacation.endDate}
                onChange={newValue => {
                  if (!selectedVacation || !newValue) return;
                  //@ts-ignore
                  setSelectedVacation(vacation => ({
                    ...vacation,
                    endDate: endOfDay(new Date(newValue))
                  }));
                }}
                renderInput={params => <TextField {...params} size="small" />}
                label={t(translations.pages.leihbarOpeningHoursSettings.vacation.endDate())}
              />
              {renderAlertBookings()}
            </Grid>
          )}
          {renderButtons()}
        </Grid>
      </form>
    );
  };

  return (
    <Grid container gap={2} justifyContent={'space-between'}>
      <Grid item xs={4}>
        <StaticDatePicker
          value={null}
          onChange={handleDateSelect}
          displayStaticWrapperAs="desktop"
          renderInput={params => <TextField {...params} />}
          minDate={new Date()}
          renderDay={(day, _, pickersDayProps) => (
            <VacationPickersDay
              day={day}
              key={day.toString()}
              pickersDayProps={pickersDayProps}
              vacations={data.vacations || []}
              bookings={bookings?.data}
            />
          )}
        />
        <AvailableColorsVacation />
      </Grid>
      <Grid item xs={7}>
        {renderDatePickers()}
      </Grid>
      <Grid container item xs justifyContent={'flex-end'}></Grid>
      <ConfirmationDialog
        title={t(translations.common.reallyDelete())}
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
      >
        {t(translations.pages.leihbarOpeningHoursSettings.vacation.confirmDelete())}
      </ConfirmationDialog>
    </Grid>
  );
};
